import React from 'react'

import { useSelector } from 'react-redux'

import { ApplicationState } from '@store/types'
import { Colors } from '@utils/Colors'
import { ColorWrapper, TextParagraph, Header } from '@atoms'
import { Button } from '@interco/inter-ui/components/Button'
import { BridgeService } from '@services/bridge'
import Tag from '@utils/Tag'

import {
  CustomGrid,
  LeftButton,
  LineButtons,
  LineButtonsWeb,
  LineDescription,
  PageImage,
  RightButton,
  VisualizarFoto,
  RootPage,
} from './styles'

interface ReviewProps {
  fotografia: string
  setHabilitaCamera: () => void
  onConfirm: (arg: string) => void
  isLoading: boolean
  forceIsMobile: boolean
}

interface ReviewPropsComp {
  fotografia: string
  setHabilitaCamera: () => void
  onConfirm: (arg: string) => void
  isLoading: boolean
  isApp?: boolean
}

const MobileView = ({
  fotografia,
  setHabilitaCamera,
  onConfirm,
  isLoading,
  isApp,
}: ReviewPropsComp) => (
  <>
    <Header showLeftIcon headerPosition="fixed">
      Foto de documento
    </Header>
    <PageImage
      id="foto-review"
      isApp
      hideHeader
      hasProgressStep={false}
      fotografia={fotografia}
      stickyFooter={
        <>
          <LineDescription>
            <ColorWrapper color={Colors.WHITE} fontWeight="600" style={{ textAlign: 'center' }}>
              <TextParagraph variant="headline-h3">
                Sua foto ficou nítida e bem iluminada?
              </TextParagraph>
            </ColorWrapper>
            <ColorWrapper color={Colors.WHITE} style={{ textAlign: 'center' }}>
              <TextParagraph variant="body-3">
                Lembre-se de que não pode ter reflexos.
              </TextParagraph>
            </ColorWrapper>
          </LineDescription>
          <LineButtons>
            <LeftButton
              size="small"
              fullWidth
              variant="secondary"
              onClick={() => {
                BridgeService.tic({
                  name: 'btn_tirar_outra_foto',
                  action: 'tirar_outra_foto',
                })

                if (isApp) {
                  Tag.enviarTagueamentoApp({
                    screen: 'clt:refin:selfie',
                    ref_type: 'botao',
                    content_action: 'Toque',
                    action_id: 'Tirar outra',
                    section: 'selfie',
                    sub_section: '',
                    step: '',
                    scenario: 'tirar outra',
                    ref_figma: '2',
                  })
                } else {
                  Tag.enviarTagueamentoWeb({
                    element_action: 'click button',
                    element_name: 'Tirar outra',
                    c_page: window.location.href,
                    redirect_url: '',
                    section_name: 'payrollloan:refin:selfie',
                    section: 'selfie',
                    sub_section: '',
                    step: '',
                    scenario: 'tirar outra',
                  })
                }

                setHabilitaCamera()
              }}
              disabled={isLoading}
            >
              Tirar outra
            </LeftButton>
            <div style={{ width: '16px' }} />
            <RightButton
              size="small"
              variant="primary"
              fullWidth
              onClick={() => {
                BridgeService.tic({
                  name: 'btn_usar_esta_foto',
                  action: 'usar_esta_foto',
                })

                if (isApp) {
                  Tag.enviarTagueamentoApp({
                    screen: 'clt:refin:selfie',
                    ref_type: 'botao',
                    content_action: 'Toque',
                    action_id: 'Usar esta',
                    section: 'selfie',
                    sub_section: '',
                    step: '',
                    scenario: 'usar esta',
                    ref_figma: '2',
                  })
                } else {
                  Tag.enviarTagueamentoWeb({
                    element_action: 'click button',
                    element_name: 'Usar esta',
                    c_page: window.location.href,
                    redirect_url: '',
                    section_name: 'payrollloan:refin:selfie',
                    section: 'selfie',
                    sub_section: '',
                    step: '',
                    scenario: 'usar esta',
                  })
                }

                onConfirm(fotografia)
              }}
              disabled={isLoading}
              isLoading={isLoading}
            >
              Usar esta
            </RightButton>
          </LineButtons>
        </>
      }
    >
      <CustomGrid>
        <ColorWrapper color={Colors.WHITE} fontWeight="600" style={{ textAlign: 'center' }}>
          <TextParagraph variant="headline-h3">Revisar foto</TextParagraph>
        </ColorWrapper>
      </CustomGrid>
    </PageImage>
  </>
)

const WebView = ({
  fotografia,
  setHabilitaCamera,
  onConfirm,
  isLoading,
  isApp: _isApp,
}: ReviewPropsComp) => (
  <RootPage id="review-web" isApp={false} hideHeader={false} hasProgressStep={false} fullPage>
    <LineDescription>
      <ColorWrapper color={Colors.GRAY500} fontWeight="600" style={{ textAlign: 'left' }}>
        <TextParagraph variant="headline-h3">Sua foto ficou nítida e bem iluminada?</TextParagraph>
      </ColorWrapper>
      <ColorWrapper color={Colors.GRAY500} style={{ textAlign: 'left' }}>
        <TextParagraph variant="body-3">Lembre-se de que não pode ter reflexos.</TextParagraph>
      </ColorWrapper>
    </LineDescription>
    <VisualizarFoto src={fotografia} alt="Fotografia" />
    <LineButtonsWeb>
      <Button
        variant="secondary"
        fullWidth
        onClick={() => {
          setHabilitaCamera()
        }}
        disabled={isLoading}
        size="small"
      >
        Tirar outra
      </Button>
      <div style={{ width: '24px' }} />
      <Button
        fullWidth
        variant="primary"
        onClick={() => {
          onConfirm(fotografia)
        }}
        disabled={isLoading}
        isLoading={isLoading}
        size="small"
      >
        Usar esta
      </Button>
    </LineButtonsWeb>
  </RootPage>
)

const ReviewFoto = ({
  fotografia,
  setHabilitaCamera,
  onConfirm,
  isLoading,
  forceIsMobile,
}: ReviewProps) => {
  const { isApp } = useSelector((state: ApplicationState) => state.ui.navigation)

  if (isApp && !BridgeService.isBrowser()) {
    BridgeService.editToolbar('', false, false)
  }

  if (isApp || forceIsMobile) {
    return (
      <MobileView
        fotografia={fotografia}
        setHabilitaCamera={setHabilitaCamera}
        onConfirm={onConfirm}
        isLoading={isLoading}
        isApp={isApp}
      />
    )
  }

  return (
    <WebView
      fotografia={fotografia}
      setHabilitaCamera={setHabilitaCamera}
      onConfirm={onConfirm}
      isLoading={isLoading}
      isApp={isApp}
    />
  )
}

export default ReviewFoto
